import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Users, Crown, HandHeart, Coins, Heart, Upload } from 'lucide-react';
import { useContactStats } from '../hooks/useContacts';
import { useCreateContact, useUpdateContact } from '../hooks/useContactMutations';
import { ContactForm } from './ContactForm';
import { ContactsTable } from './ContactsTable';
import type { Contact, ContactInsert } from '../types';

function StatCard({
  label,
  value,
  icon: Icon,
  accent = false,
}: {
  label: string;
  value: number | undefined;
  icon: React.ElementType;
  accent?: boolean;
}) {
  return (
    <div className={`card-premium rounded-2xl px-5 py-4 flex items-center gap-4 ${accent ? 'border-accent/20 shadow-glow-xs' : ''}`}>
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
        style={{
          background: accent
            ? 'linear-gradient(135deg, color-mix(in oklab, var(--color-accent), transparent 70%), color-mix(in oklab, var(--color-accent), transparent 85%))'
            : 'color-mix(in oklab, var(--color-ink), transparent 92%)',
        }}
      >
        <Icon className="h-5 w-5" style={{ color: accent ? 'var(--color-accent)' : 'var(--color-ink-muted)' }} />
      </div>
      <div>
        <p className="font-display text-2xl font-bold text-ink tabular-nums">
          {value?.toLocaleString('pt-BR') ?? '—'}
        </p>
        <p className="text-xs text-ink-muted mt-0.5">{label}</p>
      </div>
    </div>
  );
}

export function ContactsPage() {
  const [formOpen,       setFormOpen]       = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useContactStats();
  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();

  const openCreate = () => { setEditingContact(null); setFormOpen(true); };
  const openEdit   = (contact: Contact) => { setEditingContact(contact); setFormOpen(true); };
  const closeForm  = () => { setFormOpen(false); setEditingContact(null); };

  const handleSubmit = (data: ContactInsert) => {
    if (editingContact) {
      updateMutation.mutate(
        { id: editingContact.id, payload: data },
        { onSuccess: closeForm },
      );
    } else {
      createMutation.mutate(data, { onSuccess: closeForm });
    }
  };

  return (
    <div className="flex flex-col gap-8 px-4 sm:px-6 py-6 max-w-screen-xl mx-auto w-full">
      {/* ── Cabeçalho ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Contatos</h1>
          <p className="text-sm text-ink-muted mt-1">Base eleitoral, lideranças e aliados do tenant.</p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <button
            onClick={() => navigate('/app/contatos/importar')}
            className="flex items-center gap-2 btn-ghost rounded-xl px-4 py-2.5 text-sm font-semibold cursor-pointer border border-hairline"
          >
            <Upload className="h-4 w-4" />
            Importar
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 btn-accent rounded-xl px-5 py-2.5 text-sm font-semibold cursor-pointer"
          >
            <UserPlus className="h-4 w-4" />
            Novo contato
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="col-span-2 sm:col-span-3 lg:col-span-2">
          <StatCard label="Total de contatos ativos" value={statsLoading ? undefined : stats?.total} icon={Users} accent />
        </div>
        <StatCard label="Lideranças"    value={stats?.lideranca}    icon={Crown}     />
        <StatCard label="Voluntários"   value={stats?.voluntario}   icon={HandHeart} />
        <StatCard label="Doadores"      value={stats?.doador}       icon={Coins}     />
        <StatCard label="Simpatizantes" value={stats?.simpatizante} icon={Heart}     />
      </div>

      {/* ── Tabela — clique na linha abre detalhe ── */}
      <div className="card-premium rounded-2xl p-4 sm:p-6">
        <ContactsTable onEdit={openEdit} />
      </div>

      {/* ── Modal criar / editar contato (usa createPortal internamente) ── */}
      {formOpen && (
        <ContactForm
          contact={editingContact}
          onSubmit={handleSubmit}
          onClose={closeForm}
          isPending={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}
